import React, { useState, useContext, useRef } from "react";
import { useFormik } from "formik";
import { withRouter } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { ModalHeader, FormPanel, FormActions } from "../../components/SharedComp/DesignModal";
import ZoneContext from "../../Utils/Context/ZoneContext";
import FormFieldBlock from "../../components/SharedComp/FormFieldBlock";
import InputFormField from "../../components/SharedComp/InputFormField";
import SelectFields from "../../components/SharedComp/SelectFields";
import {
  zoneSchema,
  NAME_MAX,
  NUMBER_MAX,
  PATH_MAX,
} from "../../Utils/validationSchemas";
import { DESIGN_TEXT } from "../../Utils/e5Messages";

const trimValue = (v) => (typeof v === "string" ? v.trim() : v);

const AddOREditZone = (props) => {
  // context consumer
  const context = useContext(ZoneContext);
  const [NoChange, setNoChange] = useState(false);
  // errors from the last Save (409 / 400), cleared per field as the user edits it
  const [serverErrors, setServerErrors] = useState({});
  // "Your changes were not saved." beside Save
  const [formTopError, setFormTopError] = useState("");

  /**
   * state of add or edit zone component
   */
  // zone to edit: passed as a prop (Zone Configuration) or through location state (Zone Overview)
  const [updateZoneData] = useState(
    props.zoneDetails
      ? props.zoneDetails
      : props.location && props.location.state && props.location.state.zoneDetails
      ? props.location.state.zoneDetails
      : {}
  );
  const isEdit = !!(updateZoneData && updateZoneData.id);
  const saving = !!(context.loaderSave || context.dialogLoader);

  // DOM refs, used to focus the first invalid field
  const zoneNameInput = useRef(null);
  const zoneNumberInput = useRef(null);
  const zoneSCHInput = useRef(null);
  const zoneVERInput = useRef(null);
  const timeZoneInput = useRef(null);

  const initialValues = {
    zoneName: updateZoneData.zoneName ? updateZoneData.zoneName : "",
    zoneNumber: updateZoneData.zoneNumber ? updateZoneData.zoneNumber : "",
    timeZoneDetails:
      updateZoneData.timeZoneDetails && updateZoneData.timeZoneDetails.id
        ? updateZoneData.timeZoneDetails.id.toString()
        : "",
    scheduleFileBasePath: updateZoneData.scheduleFileBasePath
      ? updateZoneData.scheduleFileBasePath
      : "",
    verificationFileBasePath: updateZoneData.verificationFileBasePath
      ? updateZoneData.verificationFileBasePath
      : "",
  };

  /**
   * values sent to the API: trimmed; on edit the number is always the stored one (immutable)
   */
  const toPayload = (formValues) => ({
    zoneName: trimValue(formValues.zoneName),
    zoneNumber: isEdit ? initialValues.zoneNumber : trimValue(formValues.zoneNumber),
    timeZoneDetails: formValues.timeZoneDetails,
    scheduleFileBasePath: trimValue(formValues.scheduleFileBasePath),
    verificationFileBasePath: trimValue(formValues.verificationFileBasePath),
  });

  /**
   * submit: returns the feedback from the parent and shows 409 / 400 / 500 in the form
   */
  // one save request at a time, even if Save is hit again before the parent shows "Saving…"
  const submittingRef = useRef(false);
  const submitToParent = async (formValues) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setFormTopError("");
    let feedback;
    try {
      feedback = await context.handleSubmit(
        toPayload(formValues),
        isEdit ? updateZoneData.id : undefined
      );
    } finally {
      submittingRef.current = false;
    }
    // on success the modal closes (component unmounts), so only apply failures
    if (feedback && !feedback.ok) {
      setServerErrors(feedback.fieldErrors || {});
      setFormTopError(feedback.formError || "");
      Object.keys(feedback.fieldErrors || {}).forEach((f) => setFieldTouched(f, true, false));
    }
  };

  /**
   * formik form state
   */
  const {
    handleSubmit,
    getFieldProps,
    values,
    touched,
    errors,
    setErrors,
    setFieldTouched,
    validateForm,
  } = useFormik({
    initialValues,
    validationSchema: zoneSchema,

    /**
     * submit function of form
     */
    onSubmit(formValues) {
      if (saving) return;
      if (isEdit && !hasChanges(formValues)) {
        setNoChange(true);
        setTimeout(function () {
          setNoChange(false);
        }, 2000);
        return;
      }
      submitToParent(formValues);
    },
  });

  /**
   * Edit: any editable field differs from the stored zone (trimmed); Number is immutable.
   * Create: anything typed at all.
   */
  const hasChanges = (formValues) => {
    const current = formValues || values;
    const fields = isEdit
      ? ["zoneName", "timeZoneDetails", "scheduleFileBasePath", "verificationFileBasePath"]
      : Object.keys(initialValues);
    return fields.some(
      (f) => String(trimValue(current[f]) || "") !== String(trimValue(initialValues[f]) || "")
    );
  };

  /**
   * on Save with errors, move focus to the first invalid field (design)
   */
  const focusFirstInvalid = async () => {
    const formErrors = await validateForm();
    const order = [
      ["zoneName", zoneNameInput],
      ["zoneNumber", zoneNumberInput],
      ["timeZoneDetails", timeZoneInput],
    ];
    const first = order.find(([name]) => formErrors[name]);
    if (first && first[1].current && first[1].current.focus) {
      first[1].current.focus();
    }
  };

  const onFormSubmit = (e) => {
    handleSubmit(e);
    focusFirstInvalid();
  };

  // editing a field clears its server error (design)
  const fieldProps = (name) => {
    const fp = getFieldProps(name);
    return {
      ...fp,
      onChange: (e) => {
        if (serverErrors[name]) {
          setServerErrors({ ...serverErrors, [name]: "" });
        }
        setFormTopError("");
        fp.onChange(e);
      },
    };
  };

  // inline error: server error first, else the rule error once the field was left or Save clicked
  const fieldError = (name) =>
    serverErrors[name] || (touched[name] && errors[name] ? errors[name] : "");

  /**
   * cancel form btn and ×: ask to save when something changed, else close
   */
  const onFormCancelBtn = () => {
    if (saving) return;
    if (hasChanges()) {
      context.onClickClose();
    } else {
      props.onModalClose();
    }
  };

  // "No" in the save-before-closing dialog
  const discardAndClose = () => {
    setErrors({});
    props.onModalClose();
  };

  return (
    <div>
      <ModalHeader
        title={isEdit ? "Edit Zone" : "New Zone"}
        titleId="zone-form-title"
        onClose={onFormCancelBtn}
        disabled={saving}
      />
      <form className="baseForm" onSubmit={onFormSubmit} noValidate>
        <FormPanel>
          {/* while saving, fields stay visible but can't be edited (design) */}
          <fieldset
            disabled={saving}
            style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}
          >
            {/* design grid: Name / Number / Time Zone, then the paths full width */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "minmax(0,1.3fr) minmax(0,1fr) minmax(0,.8fr)",
                gap: "24px",
                mb: "22px",
              }}
            >
              <Box>
                <FormFieldBlock
                  label="Zone Name"
                  fieldId="zoneName"
                  required
                  value={values.zoneName}
                  max={NAME_MAX}
                  showCounter
                  error={fieldError("zoneName")}
                >
                  <InputFormField
                    {...fieldProps("zoneName")}
                    fieldType="text"
                    fieldName="zoneName"
                    maxLength={NAME_MAX}
                    aria-invalid={!!fieldError("zoneName")}
                    errors={errors}
                    touched={touched}
                    getFocus={context.hightLightFields}
                    modalClose={context.confirmModalClose}
                    refName={zoneNameInput}
                  />
                </FormFieldBlock>
              </Box>
              <Box>
                <FormFieldBlock
                  label="Zone Number"
                  fieldId="zoneNumber"
                  required
                  value={values.zoneNumber}
                  max={NUMBER_MAX}
                  showCounter={!isEdit}
                  locked={isEdit}
                  helper={isEdit ? DESIGN_TEXT.numberLocked : ""}
                  error={fieldError("zoneNumber")}
                >
                  <InputFormField
                    {...fieldProps("zoneNumber")}
                    fieldType="text"
                    fieldName="zoneNumber"
                    maxLength={NUMBER_MAX}
                    disabled={isEdit}
                    title={isEdit ? DESIGN_TEXT.numberLocked : undefined}
                    aria-invalid={!!fieldError("zoneNumber")}
                    errors={errors}
                    touched={touched}
                    getFocus={context.hightLightFields}
                    modalClose={context.confirmModalClose}
                    refName={zoneNumberInput}
                  />
                </FormFieldBlock>
              </Box>
              <Box>
                <FormFieldBlock
                  label="Time Zone"
                  fieldId="timeZoneDetails"
                  required
                  error={fieldError("timeZoneDetails")}
                >
                  <SelectFields
                    {...fieldProps("timeZoneDetails")}
                    id="timeZoneDetails"
                    fieldName="timeZoneDetails"
                    aria-invalid={!!fieldError("timeZoneDetails")}
                    timeZoneList={props.timeZoneList ? props.timeZoneList : []}
                    errors={errors}
                    touched={touched}
                    modalClose={context.confirmModalClose}
                    refName={timeZoneInput}
                  />
                </FormFieldBlock>
              </Box>
            </Box>
            <Box sx={{ mb: "22px" }}>
              <FormFieldBlock
                label="SCH Path"
                fieldId="scheduleFileBasePath"
                error={fieldError("scheduleFileBasePath")}
              >
                <InputFormField
                  {...fieldProps("scheduleFileBasePath")}
                  fieldType="text"
                  fieldName="scheduleFileBasePath"
                  maxLength={PATH_MAX}
                  errors={errors}
                  touched={touched}
                  getFocus={context.hightLightFields}
                  modalClose={context.confirmModalClose}
                  refName={zoneSCHInput}
                />
              </FormFieldBlock>
            </Box>
            <Box sx={{ mb: "22px" }}>
              <FormFieldBlock
                label="VER Path"
                fieldId="verificationFileBasePath"
                error={fieldError("verificationFileBasePath")}
              >
                <InputFormField
                  {...fieldProps("verificationFileBasePath")}
                  fieldType="text"
                  fieldName="verificationFileBasePath"
                  maxLength={PATH_MAX}
                  errors={errors}
                  touched={touched}
                  getFocus={context.hightLightFields}
                  modalClose={context.confirmModalClose}
                  refName={zoneVERInput}
                />
              </FormFieldBlock>
            </Box>
          </fieldset>
        </FormPanel>
        <FormActions
          saving={saving}
          onCancel={onFormCancelBtn}
          message={formTopError}
        />
        <Dialog
          className="confirmationDialogBox"
          open={context.dialogOpen}
          onClose={props.onModalClose}
          aria-labelledby="draggable-dialog-title"
          classes={{
            paper:
              updateZoneData && updateZoneData.id
                ? props.classesTwo.dialog
                : props.classesThree.dialog,
          }}
        >
          <DialogContent>
            <div className="confirmationText">
              Do you want to save the changed before closing?
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={discardAndClose}
              variant="contained"
              className="cancelConfirmBtn"
            >
              No
            </Button>
            <Button
              type="submit"
              color="secondary"
              variant="contained"
              onClick={handleSubmit}
            >
              {context.dialogLoader && (
                <CircularProgress
                  size={15}
                  color="info"
                  className="saveLoader"
                />
              )}{" "}
              Yes
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          className="confirmationDialogBox"
          open={NoChange}
          aria-labelledby="draggable-dialog-title"
          classes={{
            paper:
              updateZoneData && updateZoneData.id
                ? props.classesTwo.dialog
                : props.classesThree.dialog,
          }}
        >
          <DialogContent>
            <div className="confirmationText">No changes to save</div>
          </DialogContent>
        </Dialog>
      </form>
    </div>
  );
};

AddOREditZone.propTypes = {};

export default withRouter(AddOREditZone);
